import { Box, Stack, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { RefreshIcon } from '@/shared/assets/icons/refresh-icon';
import { IconBgButton } from '@/shared/components/ui/button/icon-bg-button/icon-bg-button';
import { DataTableBatchActions } from '@/shared/components/ui/data-table/components/batch-actions/data-table-batch-actions';
import { ItemCount } from '@/shared/components/ui/data-table/components/item-count/item-count';
import { ResetFiltersButton } from '@/shared/components/ui/data-table/components/reset-filters-button/reset-filters-button';
import { TableViewMenu } from '@/shared/components/ui/data-table/components/table-view-menu/table-view-menu';
import { useDataTable } from '@/shared/components/ui/data-table/context/data-table-context';
import { useDataTableSelection } from '@/shared/components/ui/data-table/hooks/use-data-table-selection/use-data-table-selection';
import { InnerPageDataTableHeaderContainer } from '@/shared/components/ui/data-table/variant/inner-data-table/components/inner-data-table-header/inner-data-table-header.styles';
import { TInnerDataTableHeaderProps } from '@/shared/components/ui/data-table/variant/inner-data-table/components/inner-data-table-header/inner-data-table-header.types';
import { QuerySearchbar } from '@/shared/components/ui/query-searchbar/query-searchbar';

export const InnerDataTableHeader = <T,>({
  searchbar,
  leftComponent,
  action,
  filters,
  filtersOnRight = false,
  batchActions,
  disableResetFilterButton = false,
  filterByLabel,
}: TInnerDataTableHeaderProps<T>) => {
  const { t } = useTranslation();
  const { unchecked } = useDataTableSelection();
  const { data } = useDataTable<T>();
  const _isLoading = data?.isLoading || data?.isFetching;

  const shouldRenderAction = () => {
    return (unchecked || !batchActions) && action;
  };

  return (
    <InnerPageDataTableHeaderContainer $isLoading={_isLoading}>
      <Stack gap="1.25rem" direction="row" alignItems="center" flex={1}>
        {batchActions && (
          <DataTableBatchActions<T> actions={batchActions} hideResultCount />
        )}
        {unchecked && (
          <>
            {leftComponent}
            <ItemCount />
            {searchbar && (
              <QuerySearchbar<T> size="small" width="240px" {...searchbar} />
            )}
            {filtersOnRight && !searchbar && <Box flex={1} />}
            {filters && (
              <Stack gap="1.25rem" direction="row" alignItems="center">
                <Typography variant="h7" color="secondary">
                  {filterByLabel ?? t('ui:filter_by')}
                </Typography>
                <Stack gap="2px" direction="row" alignItems="center">
                  {filters}
                </Stack>
                <ResetFiltersButton disabled={disableResetFilterButton} />
              </Stack>
            )}
          </>
        )}
      </Stack>
      <Stack gap="1.25rem" direction="row" alignItems="center">
        {shouldRenderAction() && action}
        {!!data?.refetch && (
          <IconBgButton
            onClick={data.refetch}
            size="extraSmall"
            variant="secondary"
            tooltip={t('common:refresh')}
          >
            <RefreshIcon />
          </IconBgButton>
        )}
        {(unchecked || !batchActions) && <TableViewMenu />}
      </Stack>
    </InnerPageDataTableHeaderContainer>
  );
};
